/**
 * Concrete {@link AuthClient} backed by `amazon-cognito-identity-js`.
 *
 * Library choice: `amazon-cognito-identity-js` (not the full `aws-amplify`).
 * Rationale — it is the lightweight, dependency-light Cognito User Pools SDK
 * that gives direct, typed access to the exact flows this package needs
 * (USER_SRP/USER_PASSWORD auth, `refreshSession`, group claims) without pulling
 * in Amplify's category framework, bundler plugins, and global singleton config.
 * The four frontend apps only need email/password sign-in and refresh-token
 * rotation (Req 23.3 / 23.6), so the smaller surface and bundle win out.
 *
 * All Cognito interaction is confined to this file; the rest of the package
 * depends only on the {@link AuthClient} interface, so unit tests use a fake.
 */
import { CognitoUserPool, CognitoUser, CognitoUserAttribute, AuthenticationDetails, } from 'amazon-cognito-identity-js';
/** Decodes the base64url payload of a JWT without verifying its signature. */
function decodeJwtPayload(token) {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1])
        return {};
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    try {
        // `atob` exists in browsers and modern Node (>=16) global scope.
        const json = atob(padded);
        return JSON.parse(json);
    }
    catch {
        return {};
    }
}
/** Builds an {@link AuthUser} from the id/access token claims. */
function userFromSession(session) {
    const idClaims = decodeJwtPayload(session.getIdToken().getJwtToken());
    const accessClaims = decodeJwtPayload(session.getAccessToken().getJwtToken());
    const rawGroups = accessClaims['cognito:groups'] ?? idClaims['cognito:groups'] ?? [];
    const groups = Array.isArray(rawGroups)
        ? rawGroups.filter((g) => typeof g === 'string')
        : [];
    return {
        userId: typeof idClaims['sub'] === 'string' ? idClaims['sub'] : '',
        username: typeof accessClaims['username'] === 'string'
            ? accessClaims['username']
            : typeof idClaims['cognito:username'] === 'string'
                ? idClaims['cognito:username']
                : '',
        email: typeof idClaims['email'] === 'string' ? idClaims['email'] : '',
        groups,
    };
}
/** Converts a Cognito SDK session into our {@link AuthSession}. */
function toAuthSession(session) {
    const accessToken = session.getAccessToken();
    return {
        accessToken: accessToken.getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        // Cognito expirations are in seconds since epoch.
        expiresAt: accessToken.getExpiration() * 1000,
        user: userFromSession(session),
    };
}
/**
 * Creates an {@link AuthClient} that talks to Amazon Cognito User Pools.
 */
export function createCognitoAuthClient(config) {
    const pool = new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.clientId,
    });
    function cognitoUser(username) {
        return new CognitoUser({ Username: username, Pool: pool });
    }
    return {
        signIn({ email, password }) {
            const user = cognitoUser(email);
            const details = new AuthenticationDetails({ Username: email, Password: password });
            return new Promise((resolve, reject) => {
                user.authenticateUser(details, {
                    onSuccess: (session) => resolve(toAuthSession(session)),
                    onFailure: (err) => reject(err),
                });
            });
        },
        signUp({ email, password }) {
            const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email })];
            return new Promise((resolve, reject) => {
                pool.signUp(email, password, attributes, [], (err) => {
                    if (err)
                        reject(err);
                    else
                        resolve();
                });
            });
        },
        confirmSignUp(email, code) {
            return new Promise((resolve, reject) => {
                cognitoUser(email).confirmRegistration(code, true, (err) => {
                    if (err)
                        reject(err);
                    else
                        resolve();
                });
            });
        },
        refreshSession(refreshToken) {
            // The current user is needed to bind the refresh call; fall back to the
            // pool's last-authenticated user.
            const current = pool.getCurrentUser();
            if (!current) {
                return Promise.reject(new Error('No current Cognito user; cannot refresh session.'));
            }
            return new Promise((resolve, reject) => {
                // CognitoRefreshToken is a tiny token wrapper; construct via the SDK.
                import('amazon-cognito-identity-js')
                    .then(({ CognitoRefreshToken }) => {
                    const token = new CognitoRefreshToken({ RefreshToken: refreshToken });
                    current.refreshSession(token, (err, session) => {
                        if (err) {
                            reject(err);
                            return;
                        }
                        resolve(toAuthSession(session));
                    });
                })
                    .catch(reject);
            });
        },
        getCurrentSession() {
            const current = pool.getCurrentUser();
            if (!current)
                return Promise.resolve(null);
            return new Promise((resolve, reject) => {
                current.getSession((err, session) => {
                    if (err) {
                        reject(err);
                        return;
                    }
                    if (!session || !session.isValid()) {
                        resolve(null);
                        return;
                    }
                    resolve(toAuthSession(session));
                });
            });
        },
        signOut() {
            const current = pool.getCurrentUser();
            return new Promise((resolve) => {
                if (!current) {
                    resolve();
                    return;
                }
                current.signOut(() => resolve());
            });
        },
    };
}
//# sourceMappingURL=cognito-client.js.map